import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  createCustomPeriod,
  distActivate,
  distCompute,
  distLoad,
  distributeUnilevel,
  getDistState,
  listPeriods,
  type DistWizardState,
} from '../lib/api';

/**
 * Phase 13.5 Distribute wizard. Each step is a numbered card; the next
 * card is enabled only once the prerequisite state has been reached on the
 * server. All transitions are idempotent — partial failures resume from
 * the last successful step. Today the wizard runs Unilevel; the same
 * shape applies to Stairstep when its admin trigger lands.
 *
 * Compute and Distribute run in the BACKGROUND (the server returns 202 and
 * a transient `computing` / `distributing` status): the state query polls
 * until the job lands on a terminal status, rendering progress from
 * `snapshot.progress` along the way.
 */
const STEPS: { key: DistWizardState['status']; label: string }[] = [
  { key: 'pending', label: '0 · Pending' },
  { key: 'activated', label: '1 · Activated' },
  { key: 'loaded', label: '2 · Loaded' },
  { key: 'computed', label: '3 · Computed' },
  { key: 'distributed', label: '4 · Distributed' },
];

function stepIndex(s: DistWizardState['status']): number {
  // Transient in-flight states sit AT the step they're working toward's
  // prerequisite: computing = still on Loaded, distributing = still on
  // Computed. The owning card shows a spinner via `running`.
  const effective = s === 'computing' ? 'loaded' : s === 'distributing' ? 'computed' : s;
  return STEPS.findIndex((x) => x.key === effective);
}

const IN_FLIGHT: Array<DistWizardState['status']> = ['computing', 'distributing'];

export function DistributePage() {
  const qc = useQueryClient();
  const periodsQ = useQuery({ queryKey: ['periods'], queryFn: listPeriods });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const plan = 'unilevel';

  const stateQ = useQuery({
    queryKey: ['dist-state', selectedId, plan],
    queryFn: () => getDistState(selectedId!, plan),
    enabled: !!selectedId,
    // Poll while a background compute/distribute owns the row; stop on
    // terminal states.
    refetchInterval: (q) =>
      q.state.data && IN_FLIGHT.includes(q.state.data.status) ? 1500 : false,
  });

  function invalidateState() {
    qc.invalidateQueries({ queryKey: ['dist-state', selectedId, plan] });
  }

  const activateMut = useMutation({
    meta: { successMessage: 'Distribution activated.' },
    mutationFn: () => distActivate(selectedId!, plan),
    onSuccess: invalidateState,
  });
  const loadMut = useMutation({
    meta: { successMessage: 'Qualifying slots loaded.' },
    mutationFn: () => distLoad(selectedId!, plan),
    onSuccess: invalidateState,
  });
  const computeMut = useMutation({
    meta: { successMessage: 'Preview compute started.' },
    mutationFn: () => distCompute(selectedId!, plan),
    onSuccess: invalidateState,
  });
  const distMut = useMutation({
    meta: { successMessage: 'Distribution started — running in the background.' },
    mutationFn: () => distributeUnilevel(selectedId!),
    onSuccess: invalidateState,
  });

  const status = stateQ.data?.status ?? 'pending';
  const idx = stepIndex(status);
  const computing = status === 'computing';
  const distributing = status === 'distributing';
  const snapshot = stateQ.data?.snapshot as
    | { progress?: { done?: number; total?: number }; error?: string }
    | undefined;
  const progress = snapshot?.progress;
  const jobError = typeof snapshot?.error === 'string' ? snapshot.error : null;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Unilevel Distribution</h1>

      <CreatePeriodCard
        onCreated={(p) => {
          setSelectedId(p.id);
          qc.invalidateQueries({ queryKey: ['periods'] });
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pick a period</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedId ?? ''} onValueChange={(v) => setSelectedId(v || null)}>
            <SelectTrigger className="w-full md:w-96 h-9">
              <SelectValue placeholder="Choose a period…" />
            </SelectTrigger>
            <SelectContent>
              {(periodsQ.data ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.code} · {p.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedId && (
        <div className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-3 flex-wrap">
                <span>Plan:</span>
                <Badge variant="outline">{plan}</Badge>
                <span className="text-sm font-normal text-muted-foreground">Current state:</span>
                <Badge>{status}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="flex flex-wrap gap-2 items-center text-xs">
                {STEPS.map((s, i) => {
                  const completed = i < idx;
                  const current = i === idx;
                  return (
                    <li
                      key={s.key}
                      className={
                        completed
                          ? 'flex items-center gap-1 px-2 py-1 rounded-md border bg-muted text-foreground'
                          : current
                            ? 'px-2 py-1 rounded-md border border-primary/40 bg-primary/10 text-primary font-medium'
                            : 'px-2 py-1 rounded-md border border-dashed text-muted-foreground'
                      }
                    >
                      {completed ? <Check className="h-3 w-3" /> : null}
                      {s.label}
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>

          <StepCard
            n={1}
            title="Activate"
            description="Lock this (period, plan) so concurrent admins can't double-trigger it."
            done={idx >= 1}
            enabled={idx === 0 || idx === 1}
            running={activateMut.isPending}
            onRun={() => activateMut.mutate()}
          />
          <StepCard
            n={2}
            title="Load qualifying slots"
            description="Snapshot the slots that meet this plan's qualification rule for the period."
            done={idx >= 2}
            enabled={idx === 1}
            running={loadMut.isPending}
            onRun={() => loadMut.mutate()}
          >
            {idx >= 2 && stateQ.data?.snapshot && (
              <SnapshotPreview snapshot={stateQ.data.snapshot} field="qualifying" />
            )}
          </StepCard>
          <StepCard
            n={3}
            title="Compute preview"
            description="Calculate per-slot expected payouts based on the snapshot. Runs in the background — this page tracks it live."
            done={idx >= 3}
            enabled={idx === 2 && !computing}
            running={computeMut.isPending || computing}
            onRun={() => computeMut.mutate()}
          >
            {computing && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Computing in the background… this can take a while on a large tenant.
              </p>
            )}
            {idx >= 3 && stateQ.data?.snapshot && (
              <SnapshotPreview snapshot={stateQ.data.snapshot} field="preview" />
            )}
          </StepCard>
          <StepCard
            n={4}
            title="Distribute"
            description="Write the ledger entries in the background. Idempotent: safe to retry."
            done={idx >= 4}
            enabled={idx === 3 && !distributing}
            running={distMut.isPending || distributing}
            danger
            onRun={() => distMut.mutate()}
          >
            {distributing && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Distributing in the background
                {progress?.total
                  ? ` — ${progress.done ?? 0}/${progress.total} events processed`
                  : '…'}
                . Leave this page open or come back later; progress is saved server-side.
              </p>
            )}
          </StepCard>

          {jobError && !computing && !distributing && (
            <Card className="border-destructive/40 bg-destructive/10">
              <CardContent className="p-4 text-sm text-destructive">
                Last background job failed: <span className="font-mono">{jobError}</span>. The step
                has been re-enabled — re-running is safe (idempotent).
              </CardContent>
            </Card>
          )}

          {idx === 4 && (
            <Card className="border-success/40 bg-success/10">
              <CardContent className="p-4 text-sm text-success">
                ✓ Distribution complete. View results under <strong>Distributions</strong> in the
                sidebar.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePeriodCard({ onCreated }: { onCreated: (p: { id: string; code: string }) => void }) {
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    meta: { successMessage: 'Period created.' },
    mutationFn: () =>
      createCustomPeriod({
        startAt: new Date(startAt).toISOString(),
        endAt: new Date(endAt).toISOString(),
        code: code || undefined,
      }),
    onSuccess: (p) => {
      onCreated(p);
      setStartAt('');
      setEndAt('');
      setCode('');
      setError(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError((e.response?.data?.error ?? 'create_failed').replace(/_/g, ' '));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Create custom period</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Distribute over an arbitrary <strong>date range</strong>: set Start/End, create the
          period, then run the steps below against it. Only orders completed inside the window are
          paid, and unilevel PPV qualification is computed from those in-window orders.
        </p>
      </CardContent>
      <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Start</label>
          <Input
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">End</label>
          <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Code (optional)</label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="2026-05" />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mut.mutate()}
            disabled={!startAt || !endAt || mut.isPending}
            className="w-full"
          >
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
        {error && <p className="md:col-span-4 text-xs text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function StepCard({
  n,
  title,
  description,
  done,
  enabled,
  running,
  onRun,
  danger,
  children,
}: {
  n: number;
  title: string;
  description: string;
  done: boolean;
  enabled: boolean;
  running: boolean;
  onRun: () => void;
  danger?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Card className={done ? 'border-emerald-500/30' : ''}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs">
                {done ? '✓' : n}
              </span>
              {title}
              {done && (
                <Badge variant="secondary" className="text-[10px]">
                  done
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{description}</div>
          </div>
          <Button
            size="sm"
            variant={danger ? 'destructive' : 'default'}
            disabled={!enabled || running}
            onClick={onRun}
          >
            {running ? 'Running…' : done ? 'Re-run' : 'Run'}
          </Button>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

type QualifyingSnapshot = {
  threshold?: string;
  qualifiers?: Array<{
    slotCode?: string;
    fullName?: string | null;
    email?: string;
    personalPv?: string;
    qualifies?: boolean;
  }>;
};
type PreviewSnapshot = {
  totalCash?: string;
  totalEvents?: number;
  recipients?: Array<{
    slotId?: string;
    slotCode?: string | null;
    ownerName?: string | null;
    totalAmount?: string;
    events?: number;
  }>;
};

function SnapshotPreview({
  snapshot,
  field,
}: {
  snapshot: Record<string, unknown>;
  field: 'qualifying' | 'preview';
}) {
  const data = snapshot[field];
  if (data == null) return null;

  if (field === 'qualifying') {
    const q = data as QualifyingSnapshot;
    const rows = q.qualifiers ?? [];
    return (
      <div className="mt-2 space-y-2">
        <p className="text-[11px] text-muted-foreground">
          Qualification PV threshold: <span className="font-mono">{q.threshold ?? '0'}</span> ·{' '}
          {rows.filter((r) => r.qualifies).length}/{rows.length} qualify
        </p>
        {rows.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">
            No slots had purchases in this window.
          </p>
        ) : (
          <div className="overflow-x-auto border rounded-md max-h-60 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-left text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 font-medium">Slot</th>
                  <th className="px-2 py-1.5 font-medium">Member</th>
                  <th className="px-2 py-1.5 font-medium text-right">Personal PV</th>
                  <th className="px-2 py-1.5 font-medium text-center">Qualifies</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1 font-mono">{r.slotCode}</td>
                    <td className="px-2 py-1 text-muted-foreground">{r.fullName || r.email}</td>
                    <td className="px-2 py-1 text-right font-mono">
                      {Number(r.personalPv ?? 0).toFixed(2)}
                    </td>
                    <td className="px-2 py-1 text-center">{r.qualifies ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  const p = data as PreviewSnapshot;
  const rows = p.recipients ?? [];
  return (
    <div className="mt-2 space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Projected payout (dry-run): <span className="font-mono">{p.totalCash ?? '0.00'}</span>{' '}
        across {rows.length} recipient{rows.length === 1 ? '' : 's'} · {p.totalEvents ?? 0} order
        {p.totalEvents === 1 ? '' : 's'} in window
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Nothing to pay. No qualifying uplines for the in-window orders.
        </p>
      ) : (
        <div className="overflow-x-auto border rounded-md max-h-60 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-left text-muted-foreground sticky top-0">
              <tr>
                <th className="px-2 py-1.5 font-medium">Recipient slot</th>
                <th className="px-2 py-1.5 font-medium">Owner</th>
                <th className="px-2 py-1.5 font-medium text-right">Projected amount</th>
                <th className="px-2 py-1.5 font-medium text-right">Orders</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1 font-mono">{r.slotCode ?? r.slotId?.slice(0, 8)}</td>
                  <td className="px-2 py-1 text-muted-foreground">{r.ownerName ?? ''}</td>
                  <td className="px-2 py-1 text-right font-mono">
                    {Number(r.totalAmount ?? 0).toFixed(2)}
                  </td>
                  <td className="px-2 py-1 text-right">{r.events ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
